import { useState, useCallback } from 'react'
import { fetchWithAuth } from '@/lib/api/client'
import { registerPasskey, authenticatePasskey } from '@/lib/crypto/webauthn'
import {
  createWallet,
  importWallet,
  encryptWallet,
  decryptWallet,
  getChainName,
} from '@/lib/crypto/wallet'
import type { WalletInfo, EncryptedWalletData } from '@/lib/crypto/wallet'
import { getCachedWallet, setCachedWallet, clearWalletCache } from '@/lib/wallet-utils'

const API_URL = import.meta.env.VITE_API_URL!

export interface UseWalletReturn {
  wallet: WalletInfo | null
  isLoading: boolean
  error: string | null
  hasWallet: boolean
  publicAddress: string | null
  chainName: string

  // Actions
  generateWallet: (userName: string, userId: string) => Promise<WalletInfo>
  restoreWallet: (privateKey: string, userName: string, userId: string) => Promise<WalletInfo>
  loadWallet: (userId: string) => Promise<void>
  deleteWallet: () => Promise<void>
  exportPrivateKey: () => Promise<string>
  clearWallet: () => void
  initializeWalletState: () => Promise<void>
}

export function useWallet(): UseWalletReturn {
  const [wallet, setWallet] = useState<WalletInfo | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [hasWallet, setHasWallet] = useState(false)
  const [publicAddress, setPublicAddress] = useState<string | null>(null)
  const chainName = getChainName()

  /**
   * Check if a wallet exists on the backend and get public address
   */
  const checkWalletExists = useCallback(async (): Promise<{ exists: boolean; address?: string }> => {
    try {
      const response = await fetchWithAuth(`${API_URL}/api/wallet/exists`, {
        method: 'GET',
      })

      if (!response.ok) {
        return { exists: false }
      }

      const data = await response.json()
      return {
        exists: data.exists || false,
        address: data.address
      }
    } catch (err) {
      console.error('Error checking wallet existence:', err)
      return { exists: false }
    }
  }, [])

  /**
   * Generate a new wallet with passkey protection
   */
  const generateWallet = useCallback(
    async (userName: string, userId: string): Promise<WalletInfo> => {
      setIsLoading(true)
      setError(null)

      try {
        // Check if wallet already exists
        const { exists } = await checkWalletExists()
        if (exists) {
          throw new Error('Wallet already exists. Delete it first or use /restore.')
        }

        // Create new wallet
        const newWallet = createWallet()

        // Step 1: Register passkey
        // This will throw an error if PRF is not supported on the device
        // On some browsers (Chrome, Edge), we may get the PRF key immediately
        const { credentialId, prfKey: prfKeyFromRegistration } = await registerPasskey(userName, userId)

        // Step 2: Get PRF key (either from registration or by authenticating)
        let prfKey: ArrayBuffer
        if (prfKeyFromRegistration) {
          // Lucky! Browser supports PRF eval during registration (single prompt)
          prfKey = prfKeyFromRegistration
        } else {
          // Browser doesn't support PRF eval during registration
          // Need to authenticate to get the PRF key (second prompt)
          const authResult = await authenticatePasskey(credentialId)
          if (!authResult.prfKey) {
            throw new Error('Failed to obtain PRF key from passkey')
          }
          prfKey = authResult.prfKey
        }

        // Step 3: Encrypt wallet with PRF key
        const encryptedWallet = await encryptWallet(
          newWallet,
          prfKey,
          credentialId
        )

        // Save to backend
        const response = await fetchWithAuth(`${API_URL}/api/wallet`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(encryptedWallet),
        })

        if (!response.ok) {
          const errorData = await response.json()
          throw new Error(errorData.error || 'Failed to save wallet')
        }

        // Set wallet in state and cache for marketplace operations
        setWallet(newWallet)
        setHasWallet(true)
        setPublicAddress(newWallet.address)
        setCachedWallet(newWallet)

        return newWallet
      } catch (err) {
        const message =
          err instanceof Error ? err.message : 'Failed to generate wallet'
        setError(message)
        throw err
      } finally {
        setIsLoading(false)
      }
    },
    [checkWalletExists]
  )

  /**
   * Restore wallet from private key with passkey protection
   */
  const restoreWallet = useCallback(
    async (
      privateKey: string,
      userName: string,
      userId: string
    ): Promise<WalletInfo> => {
      setIsLoading(true)
      setError(null)

      try {
        // Import wallet from private key
        const restoredWallet = importWallet(privateKey)

        // Step 1: Register new passkey for the restored wallet
        // This will throw an error if PRF is not supported on the device
        // On some browsers (Chrome, Edge), we may get the PRF key immediately
        const { credentialId, prfKey: prfKeyFromRegistration } = await registerPasskey(userName, userId)

        // Step 2: Get PRF key (either from registration or by authenticating)
        let prfKey: ArrayBuffer
        if (prfKeyFromRegistration) {
          // Lucky! Browser supports PRF eval during registration (single prompt)
          prfKey = prfKeyFromRegistration
        } else {
          // Browser doesn't support PRF eval during registration
          // Need to authenticate to get the PRF key (second prompt)
          const authResult = await authenticatePasskey(credentialId)
          if (!authResult.prfKey) {
            throw new Error('Failed to obtain PRF key from passkey')
          }
          prfKey = authResult.prfKey
        }

        // Step 3: Encrypt wallet with new PRF key
        const encryptedWallet = await encryptWallet(
          restoredWallet,
          prfKey,
          credentialId
        )

        // Step 4: Save to backend
        const method = 'POST'
        const requestBody = encryptedWallet

        const response = await fetchWithAuth(`${API_URL}/api/wallet`, {
          method,
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(requestBody),
        })

        if (!response.ok) {
          const errorData = await response.json()
          throw new Error(errorData.error || 'Failed to save wallet')
        }

        // Set wallet in state and cache for marketplace operations
        setWallet(restoredWallet)
        setHasWallet(true)
        setPublicAddress(restoredWallet.address)
        setCachedWallet(restoredWallet)

        return restoredWallet
      } catch (err) {
        const message =
          err instanceof Error ? err.message : 'Failed to restore wallet'
        setError(message)
        throw err
      } finally {
        setIsLoading(false)
      }
    },
    [checkWalletExists]
  )

  /**
   * Load wallet from backend and decrypt with passkey
   */
  const loadWallet = useCallback(async (userId: string): Promise<void> => {
    setIsLoading(true)
    setError(null)

    try {
      // Check if wallet is already cached in memory
      const cached = getCachedWallet()
      if (cached) {
        setWallet(cached)
        setHasWallet(true)
        setPublicAddress(cached.address)
        setIsLoading(false)
        return
      }

      // Check if wallet exists and get public address
      const { exists, address } = await checkWalletExists()
      setHasWallet(exists)
      setPublicAddress(address || null)

      if (!exists) {
        setWallet(null)
        return
      }

      // Fetch encrypted wallet from backend
      const response = await fetchWithAuth(`${API_URL}/api/wallet`, {
        method: 'GET',
      })

      if (!response.ok) {
        if (response.status === 404) {
          setHasWallet(false)
          setWallet(null)
          return
        }
        throw new Error('Failed to fetch wallet')
      }

      const data = await response.json()
      const encryptedWallet: EncryptedWalletData = data.wallet

      // Authenticate with passkey to get PRF key
      // This will throw an error if PRF is not available
      const { prfKey } = await authenticatePasskey(
        encryptedWallet.credentialId
      )

      if (!prfKey) {
        throw new Error('Failed to obtain PRF key from passkey')
      }

      // Decrypt wallet with PRF key
      const decryptedWallet = await decryptWallet(encryptedWallet, prfKey)

      // Set wallet in state and cache for marketplace operations
      setWallet(decryptedWallet)
      setHasWallet(true)
      setCachedWallet(decryptedWallet)
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Failed to load wallet'
      setError(message)
      setWallet(null)
      throw err
    } finally {
      // it takes a sec for the wallet to be visible
      await new Promise(resolve => setTimeout(resolve, 1000))
      setIsLoading(false)
    }
  }, [checkWalletExists])

  /**
   * Delete wallet from backend
   */
  const deleteWallet = useCallback(async (): Promise<void> => {
    setIsLoading(true)
    setError(null)

    try {
      const response = await fetchWithAuth(`${API_URL}/api/wallet`, {
        method: 'DELETE',
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.error || 'Failed to delete wallet')
      }

      // Clear wallet from state and cache
      setWallet(null)
      setHasWallet(false)
      setPublicAddress(null)
      clearWalletCache()
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Failed to delete wallet'
      setError(message)
      throw err
    } finally {
      setIsLoading(false)
    }
  }, [])

  /**
   * Export private key (requires re-authentication with passkey)
   */
  const exportPrivateKey = useCallback(async (): Promise<string> => {
    if (!wallet) {
      throw new Error('No wallet loaded')
    }

    // Re-authenticate with passkey for security
    try {
      await authenticatePasskey()
      return wallet.privateKey
    } catch (err) {
      throw new Error('Authentication failed. Cannot export private key.')
    }
  }, [wallet])

  /**
   * Clear wallet from memory (does not delete from backend)
   */
  const clearWallet = useCallback(() => {
    setWallet(null)
    setError(null)
    clearWalletCache()
  }, [])

  /**
   * Initialize wallet state by checking if a wallet exists
   * Does not load or decrypt the wallet
   */
  const initializeWalletState = useCallback(async (): Promise<void> => {
    try {
      const { exists, address } = await checkWalletExists()
      setHasWallet(exists)
      setPublicAddress(address || null)
    } catch (err) {
      console.error('Error initializing wallet state:', err)
      setHasWallet(false)
      setPublicAddress(null)
    }
  }, [checkWalletExists])

  return {
    wallet,
    isLoading,
    error,
    hasWallet,
    publicAddress,
    chainName,
    generateWallet,
    restoreWallet,
    loadWallet,
    deleteWallet,
    exportPrivateKey,
    clearWallet,
    initializeWalletState,
  }
}
